import { OrbitControls as ThreeOrbitControls } from '@react-three/drei'
import type {
  OrbitControlsChangeEvent as ThreeOrbitControlsChangeEvent,
  OrbitControlsProps as ThreeOrbitControlsProps,
} from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { Vector3 } from 'three'

import { DEFAULT_CAMERA_CONTROLS, DEFAULT_CANVAS_CONTROLS } from '../constants'
import SpringAnimate from '../util/spring-animate'

export interface OrbitControlsRefProps {
  spawnCamera: (position: Vector3, target: Vector3) => void
  setPosition: (position: Vector3) => void
  setTarget: (target: Vector3) => void
}

export interface OrbitControlsProps {
  getPosition?: (position: Vector3) => void
  getTarget?: (target: Vector3) => void
  detectChange?: (arg: boolean) => void
  initPosition: Vector3
  initTarget: Vector3
  isMoving?: (arg: boolean) => void
  isCameraLoaded?: (arg: boolean) => void
  maxDistance?: number
  minDistance?: number
  minPolarAngle?: number
  maxPolarAngle?: number
  enablePan?: boolean
  disableCameraLoadingCntrl?: boolean
  near?: number
  far?: number
}

const OrbitControls = forwardRef<
  OrbitControlsRefProps | undefined,
  OrbitControlsProps
>(
  (
    {
      getTarget,
      getPosition,
      detectChange,
      initPosition,
      initTarget,
      isMoving,
      maxDistance = DEFAULT_CAMERA_CONTROLS.maxDistance,
      minDistance = DEFAULT_CAMERA_CONTROLS.minDistance,
      minPolarAngle = DEFAULT_CAMERA_CONTROLS.minPolarAngle,
      maxPolarAngle = DEFAULT_CAMERA_CONTROLS.maxPolarAngle,
      isCameraLoaded,
      enablePan = false,
      disableCameraLoadingCntrl = false,
      near,
      far,
    },
    ref
  ) => {
    const orbitRef = useRef(null)
    const isUserInteracting = useRef(false)

    const { camera } = useThree()

    const springAnimate = SpringAnimate({
      initTarget: [initTarget.x, initTarget.y, initTarget.z],
      initPosition: [initPosition.x, initPosition.y, initPosition.z],
    })

    const distanceLimits = React.useMemo(() => {
      const isValidDistance = maxDistance > minDistance

      return {
        maxDistance: isValidDistance
          ? maxDistance
          : DEFAULT_CAMERA_CONTROLS.maxDistance,
        minDistance: isValidDistance
          ? minDistance
          : DEFAULT_CAMERA_CONTROLS.minDistance,
      }
    }, [maxDistance, minDistance])

    const setPosition = (pos: Vector3) => {
      springAnimate.setPosition([pos?.x || 0, pos?.y || 0, pos?.z || 0])
    }

    const setTarget = (pos: Vector3) => {
      springAnimate.setTarget([pos?.x || 0, pos?.y || 0, pos?.z || 0])
    }

    const setSpringAnimationTarget = (currentTarget: Vector3) => {
      if (currentTarget) {
        springAnimate.springs.target.set([
          currentTarget?.x || 0,
          currentTarget?.y || 0,
          currentTarget?.z || 0,
        ])
      }
    }

    const setSpringAnimationPosition = (currentPosition: Vector3) => {
      if (currentPosition) {
        springAnimate.springs.position.set([
          currentPosition?.x || 0,
          currentPosition?.y || 0,
          currentPosition?.z || 0,
        ])
      }
    }

    const spawnCamera = (pos: Vector3, tar: Vector3) => {
      if (camera) {
        setTarget(tar)
        setSpringAnimationTarget(tar)
        setPosition(pos)
        setSpringAnimationPosition(pos)
        camera.position.set(pos.x, pos.y, pos.z)
      }
    }

    useImperativeHandle(ref, () => ({
      spawnCamera,
      setPosition,
      setTarget,
    }))

    React.useEffect(() => {
      if (orbitRef.current) {
        const orbitControl = orbitRef?.current as ThreeOrbitControlsProps
        if (orbitControl) {
          orbitControl.target = initTarget
        }
      }
    }, [initTarget, orbitRef])

    React.useEffect(() => {
      if (camera) {
        camera.position.set(initPosition.x, initPosition.y, initPosition.z)
      }
    }, [initPosition])

    React.useEffect(() => {
      if (!camera) {
        return
      }

      camera.near = near ?? DEFAULT_CANVAS_CONTROLS.near
      camera.far = far ?? DEFAULT_CANVAS_CONTROLS.far
      camera.updateProjectionMatrix()
    }, [camera, near, far])

    const handleOnStart = useCallback(() => {
      isUserInteracting.current = true
    }, [])

    const handleGetTarget = (target: Vector3) => {
      if (getTarget) {
        getTarget(target)
      }
    }

    const handleGetPosition = useCallback(
      (position: Vector3) => {
        if (getPosition) {
          getPosition(position)
        }
      },
      [getPosition]
    )

    const handleDetectPosition = () => {
      if (detectChange) {
        detectChange(true)
      }
    }

    const handleOnEnd = useCallback(() => {
      isUserInteracting.current = false
      if (isMoving) {
        isMoving(false)
      }
      if (orbitRef?.current) {
        const position = (
          orbitRef?.current as ThreeOrbitControlsProps
        ).object?.getWorldPosition(new Vector3())
        const target = (orbitRef?.current as ThreeOrbitControlsProps)
          .target as Vector3
        if (target) {
          handleGetTarget(target)
          setSpringAnimationTarget(target)
        }
        if (position) {
          handleDetectPosition()
          setSpringAnimationPosition(position)
        }
      }
    }, [handleGetTarget])

    const handleOnChange = useCallback(
      (e: ThreeOrbitControlsChangeEvent | undefined) => {
        if (isMoving && isUserInteracting.current) {
          isMoving(true)
        }

        if (isUserInteracting.current && e) {
          const position = e.target?.object?.getWorldPosition(new Vector3())
          if (position) {
            handleGetPosition(position)
          }
        }
      },
      [handleGetPosition, isMoving]
    )

    React.useEffect(() => {
      if (camera) {
        setSpringAnimationPosition(initPosition)
        camera.position.set(initPosition.x, initPosition.y, initPosition.z)
        if (isCameraLoaded && !disableCameraLoadingCntrl) {
          isCameraLoaded(true)
        }
      }
    }, [isCameraLoaded, disableCameraLoadingCntrl, camera])

    return (
      <ThreeOrbitControls
        camera={camera}
        target={initTarget}
        enablePan={enablePan}
        enableDamping={false}
        ref={orbitRef}
        onStart={() => handleOnStart()}
        onEnd={() => handleOnEnd()}
        onChange={handleOnChange}
        minDistance={distanceLimits.minDistance}
        maxDistance={distanceLimits.maxDistance}
        minPolarAngle={minPolarAngle}
        maxPolarAngle={maxPolarAngle}
        makeDefault
      />
    )
  }
)

export default OrbitControls
